import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ThreatTypes } from '../../helpers/types';
import { formatedDate } from '../../utils';
import { getDisplayThreatName, getThreatUrl } from '../../utils/domainUtils';
import { getNoteIdApi, getNotesValuesApi } from '../../queries/noteComments';
import ImageSkeleton from '../PlaceholderSkeleton/ImageSkeleton';
import Avtar from '../../assets/Images/Avtar.png';
import {
  MaliciousText,
  NoMaliciousText,
  SuspiciousText,
  UnknownText,
  BenignText,
} from '../../pages/ThreadsDetails/Style/SingleThreat.style';
import useThreatExpandData from './useThreatExpandData';
import { ScreenshotThumb } from '../Screenshot';
import { useExpandedDerivedData } from '../ExpandedRow';

/** Inline kbd chip marking a keyboard shortcut on a control. */
export const Kbd = ({ k, sx }: { k: string; sx?: Record<string, unknown> }) => (
  <Typography
    component="kbd"
    variant="caption"
    sx={{
      ml: 0.75,
      px: 0.5,
      border: 1,
      borderColor: 'divider',
      borderRadius: '4px',
      lineHeight: 1.4,
      ...sx,
    }}
  >
    {k}
  </Typography>
);

export const VERDICT_OPTIONS: Array<{
  status: string;
  label: string;
  shortcut: string;
}> = [
  { status: 'confirmed', label: 'Confirm', shortcut: 'Y' },
  { status: 'discarded', label: 'Discard', shortcut: 'D' },
  { status: 'benign', label: 'Benign', shortcut: 'B' },
  { status: 'trademark', label: 'Trademark', shortcut: 'M' },
  { status: 'owned', label: 'Owned', shortcut: 'W' },
];

interface ThreatNoteItem {
  id: number;
  note_id: number;
  auth0_user_id: string;
  created_at: string;
}

interface NotesSummary {
  items: ThreatNoteItem[];
  total: number;
  loading: boolean;
}

/**
 * Fetches a short notes summary (latest few notes + total) for the focused
 * threat, plus the static note-value labels (fetched once per mount).
 */
const useThreatNotesSummary = (
  threat: ThreatTypes,
): NotesSummary & {
  noteLabel: (noteId: number) => string;
} => {
  const [items, setItems] = useState<ThreatNoteItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    let cancelled = false;
    getNotesValuesApi()
      .then((res: any) => {
        if (cancelled) return;
        const map = new Map<number, string>();
        (res?.data ?? []).forEach((category: any) => {
          (category?.note_values ?? []).forEach((value: any) => {
            map.set(value.id, value.name);
          });
        });
        setLabels(map);
      })
      .catch(() => {
        // labels are cosmetic — fall back to "Note #id"
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setTotal(0);
    getNoteIdApi({
      domain_id: threat.protected_asset_id,
      threat_id: threat.threat_id,
      page: 1,
      size: 3,
    })
      .then((res: any) => {
        if (cancelled) return;
        setItems(res?.data?.items ?? []);
        setTotal(res?.data?.total ?? 0);
      })
      .catch(() => {
        // notes are supplementary — the panel stays useful without them
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [threat.threat_id, threat.protected_asset_id]);

  const noteLabel = (noteId: number) => labels.get(noteId) ?? `Note #${noteId}`;

  return { items, total, loading, noteLabel };
};

const PRE_CRIME_COMPONENTS: Record<string, React.ReactNode> = {
  malicious: <MaliciousText>Malicious</MaliciousText>,
  suspicious: <SuspiciousText>Suspicious</SuspiciousText>,
  unknown: <UnknownText>Unknown</UnknownText>,
  benign: <BenignText>Benign</BenignText>,
};

const FactRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ minWidth: 130, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-all' }}>
      {children}
    </Typography>
  </Box>
);

interface ThreatPreviewContentProps {
  threat: ThreatTypes;
  whois?: {
    registrar?: string;
    registration_date?: string;
    expiration_date?: string;
  };
  whoisLoading?: boolean;
  companyName: string;
  onVerdict: (threat: ThreatTypes, status: string) => void;
}

const ThreatPreviewContent = ({
  threat,
  whois,
  whoisLoading,
  companyName,
  onVerdict,
}: ThreatPreviewContentProps) => {
  const {
    threatDetail,
    gsbLiveInfo,
    dnsRecords,
    isLoading,
    gsbLoading,
    dnsLoading,
  } = useThreatExpandData(threat, true);

  const score = threatDetail?.score?.value ?? threat.score?.value ?? 0;
  // Prefer the row's status: the redux list is updated optimistically on
  // verdicts, while threatDetail can be a stale per-instance cache entry.
  const status =
    threat.identification_status?.value ??
    threatDetail?.identification_status?.value;

  const { preCrimeLevel, gsbThreatTypes, dnsARecord } = useExpandedDerivedData({
    score,
    gsbLiveInfo,
    dnsRecords,
    statusHistory: undefined,
    takedownStatuses: undefined,
  });

  const notes = useThreatNotesSummary(threat);

  const detailUrl = `/threats/${threat.protected_asset_id}/${threat.threat_id}/${threat.company_id}`;
  const displayName = getDisplayThreatName(getThreatUrl(threat));

  const copyThreatName = () => {
    try {
      navigator.clipboard.writeText(getThreatUrl(threat));
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}
        >
          {displayName}
        </Typography>
        <Tooltip title="Copy threat name">
          <IconButton
            size="small"
            onClick={copyThreatName}
            data-testid="threats-preview-copy"
          >
            <ContentCopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open full details in new tab">
          <IconButton
            size="small"
            onClick={() => window.open(detailUrl, '_blank')}
            data-testid="threats-preview-open-detail"
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ my: 1 }}>
        <ScreenshotThumb
          threatId={threat.threat_id}
          width="100%"
          skeletonHeight={180}
          testId="threats-preview-screenshot"
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <FactRow label="Status:">
          {isLoading ? <Skeleton variant="text" width={80} /> : status || 'N/A'}
        </FactRow>
        <FactRow label="PreCrime Network:">
          {isLoading ? (
            <Skeleton variant="text" width={80} />
          ) : (
            <>
              {status === 'confirmed' ? (
                PRE_CRIME_COMPONENTS[preCrimeLevel]
              ) : (
                <NoMaliciousText>No Classification</NoMaliciousText>
              )}{' '}
              ({Number(score).toFixed(3)})
            </>
          )}
        </FactRow>
        <FactRow label="Google Safe Browsing:">
          {gsbLoading ? (
            <Skeleton variant="text" width={80} />
          ) : gsbThreatTypes.length > 0 ? (
            <MaliciousText>
              Malicious ({gsbThreatTypes.join(', ')})
            </MaliciousText>
          ) : (
            <NoMaliciousText>No Classification</NoMaliciousText>
          )}
        </FactRow>
        <FactRow label="DNS A Record:">
          {dnsLoading ? <Skeleton variant="text" width={80} /> : dnsARecord}
        </FactRow>
        <FactRow label="Company:">{companyName}</FactRow>
        <FactRow label="Protected asset:">
          {threat.protected_asset_name}
        </FactRow>
        <FactRow label="Registrar:">
          {whoisLoading ? (
            <Skeleton variant="text" width={80} />
          ) : (
            whois?.registrar || 'No data available'
          )}
        </FactRow>
        <FactRow label="Registration:">
          {whoisLoading ? (
            <Skeleton variant="text" width={80} />
          ) : whois?.registration_date ? (
            `${formatedDate(whois.registration_date)} UTC`
          ) : (
            'No data available'
          )}
        </FactRow>
        <FactRow label="Detection:">
          {threat.detection_date
            ? `${formatedDate(threat.detection_date)} UTC`
            : 'No data available'}
        </FactRow>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box data-testid="threats-preview-notes">
        <Typography variant="caption" color="text.secondary">
          Notes {notes.loading ? '' : `(${notes.total})`}
        </Typography>
        {notes.loading ? (
          <Skeleton variant="text" width="60%" />
        ) : notes.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No notes yet
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {notes.items.map(note => (
              <Typography key={note.id} variant="body2">
                {notes.noteLabel(note.note_id)}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  — {note.auth0_user_id}, {formatedDate(note.created_at)} UTC
                </Typography>
              </Typography>
            ))}
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {VERDICT_OPTIONS.map(verdict => (
          <Button
            key={verdict.status}
            size="small"
            variant={verdict.status === 'confirmed' ? 'contained' : 'outlined'}
            disabled={status === verdict.status}
            onClick={() => onVerdict(threat, verdict.status)}
            data-testid={`threats-preview-verdict-${verdict.status}`}
            sx={{ textTransform: 'none' }}
          >
            {verdict.label}
            <Kbd k={verdict.shortcut} />
          </Button>
        ))}
      </Box>
    </>
  );
};

interface ThreatPreviewPanelProps {
  threat: ThreatTypes | null;
  whois?: {
    registrar?: string;
    registration_date?: string;
    expiration_date?: string;
  };
  whoisLoading?: boolean;
  companyName: string;
  onVerdict: (threat: ThreatTypes, status: string) => void;
  onClose: () => void;
}

const ThreatPreviewPanel = ({
  threat,
  whois,
  whoisLoading,
  companyName,
  onVerdict,
  onClose,
}: ThreatPreviewPanelProps) => (
  <Box
    data-testid="threats-preview-panel"
    sx={{
      width: 400,
      flexShrink: 0,
      position: 'sticky',
      top: 76,
      maxHeight: 'calc(100vh - 96px)',
      overflowY: 'auto',
      border: theme => `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      // Tertiary + shadow so the panel reads as its own widget — paper
      // blends into the table card it sits next to.
      bgcolor: 'background.tertiary',
      boxShadow: 3,
      p: 1.5,
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Threat preview
        <Kbd k="P" />
      </Typography>
      <Tooltip title="Close preview panel (P)">
        <IconButton
          size="small"
          onClick={onClose}
          data-testid="threats-preview-close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
    {threat ? (
      // Key by threat so per-threat local state (notes) resets predictably;
      // useThreatExpandData's internal cache lives per instance, so this
      // stays a single mounted subtree while the panel is open.
      <ThreatPreviewContent
        threat={threat}
        whois={whois}
        whoisLoading={whoisLoading}
        companyName={companyName}
        onVerdict={onVerdict}
      />
    ) : (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="threats-preview-empty"
      >
        Focus a row with ↑/↓ or J/K to preview it here. Verdicts: Y confirm, D
        discard, M trademark, W owned.
      </Typography>
    )}
  </Box>
);

export default ThreatPreviewPanel;
